import { ComponentProps, FC, ReactNode } from 'react'
import {
  Box,
  Card,
  Flex,
  Grid,
  GreyGrey10,
  GreyGrey70,
  InputSelectControlField,
  Link,
  MultiSelectControlField,
  Primary12Regular,
  Primary13SemiBold,
  Primary14SemiBold,
  Skeleton,
  Spinner,
  Switch,
  SwitchLineInput
} from '@sequencehq/core-components'
import { IntegrationMoreInfo } from 'lib/docsLink/integrations'
import { NotConnectedItemContent } from 'modules/Integrations/integrationsConfig/common/NotConnectedItemContent'
import { SmallIcon } from 'modules/Integrations/integrationsConfig/common/SmallIcon'
import { WorkflowManagement } from 'modules/Integrations/integrationsConfig/common/WorkflowManagement'
import {
  SETTINGS_FIELD_WIDTH,
  SETTINGS_PLACEHOLDER_HEIGHT
} from 'modules/Integrations/integrationsConfig/hubSpot/hubSpot.constants'
import { useHubSpotConfigurationManagement } from './useHubSpotConfigurationManagement'
import { ConfigurationManagementProps } from 'Integrations/domain'

export const HubSpotConfigurationManagement = (
  props: ConfigurationManagementProps
) => {
  const { fieldsConfig, loading, showNotConnected, showSpinner, showSettings } =
    useHubSpotConfigurationManagement(props)

  if (showSpinner) {
    return (
      <Flex
        data-testid="hubspot.connectingSpinner"
        width="100%"
        height={SETTINGS_PLACEHOLDER_HEIGHT}
        alignItems="center"
        justifyContent="center"
      >
        <Spinner color={GreyGrey70} />
      </Flex>
    )
  }

  if (showNotConnected) {
    return (
      <Flex width="100%" minHeight={SETTINGS_PLACEHOLDER_HEIGHT}>
        <NotConnectedItemContent
          data-testid="hubspot.notConnected"
          service="HubSpot"
          serviceName={props.integration.title}
          serviceDescription={props.integration.description}
          width="100%"
          logo={<SmallIcon url={props.integration.smallLogoUrl} />}
        />
      </Flex>
    )
  }

  if (!showSettings) {
    return (
      <Flex flexDirection="column" gap="20px" width="100%">
        <Card width="100%">
          <Grid gridRowGap="16px">
            <Box {...Primary14SemiBold}>Push data into Sequence</Box>
            <WorkflowManagement
              logoUrls={fieldsConfig.importCustomerWorkflow.logoUrls}
              status={fieldsConfig.importCustomerWorkflow.status}
              label="Link customers to HubSpot"
              description="Enables the link to HubSpot option in the customers page."
              onClick={() => {
                fieldsConfig.importCustomerWorkflow.onChange(
                  !fieldsConfig.importCustomerWorkflow.value
                )
              }}
            >
              <Flex alignContent="center" onClick={e => e.stopPropagation()}>
                <Switch
                  data-testid="switch.linkCustomerWorkflow"
                  checked={fieldsConfig.importCustomerWorkflow.value}
                  onChange={fieldsConfig.importCustomerWorkflow.onChange}
                />
              </Flex>
            </WorkflowManagement>
          </Grid>
        </Card>
      </Flex>
    )
  }

  if (loading) {
    return (
      <Flex flexDirection="column" gap="20px" width="100%">
        <Skeleton width="100%" height={SETTINGS_PLACEHOLDER_HEIGHT} />
      </Flex>
    )
  }

  return (
    <Flex
      data-testid="hubspotIntegration.configurationForm"
      flexDirection="column"
      gap="20px"
      width="100%"
    >
      <SettingsSection title="Customers">
        <MultiSelectControlField
          data-testid="select.customerImportStages"
          label="Customer import stages"
          description="Configure the deal stages that trigger companies to be created in Sequence."
          tooltip={{
            content: (
              <>
                Companies missing certain fields, such as address lines,
                country, or zip codes may fail to sync automatically (
                <Link
                  data-testid="hubspot.customerImportStages.learnMore"
                  to={IntegrationMoreInfo.HubSpotSyncingCustomersLink}
                  target="_blank"
                  textDecoration="underline"
                >
                  learn more
                </Link>
                ).
              </>
            )
          }}
          width={SETTINGS_FIELD_WIDTH}
          entityName="stage"
          options={fieldsConfig.customerImportStages.options}
          values={fieldsConfig.customerImportStages.value}
          onChange={fieldsConfig.customerImportStages.onChange}
        />
      </SettingsSection>
      <SettingsSection title="Quotes">
        <SettingSwitchRow
          text="Sync quote value to deal amount"
          description="Automatically sync quote's total contract value to the deal amount in HubSpot."
          data-testid="switch.dealAmountSync"
          value={fieldsConfig.dealAmountSync.value}
          onChange={fieldsConfig.dealAmountSync.onChange}
        />
        <SettingSwitchRow
          text="Sync quote PDF on signing"
          description="Automatically sync the signed quote PDF to the related deal in HubSpot."
          data-testid="switch.pdfSyncOnSign"
          value={fieldsConfig.pdfSyncOnSign.value}
          onChange={fieldsConfig.pdfSyncOnSign.onChange}
        />
        <InputSelectControlField
          styles={{
            wrapper: {
              marginBottom: 0,
              maxWidth: SETTINGS_FIELD_WIDTH
            }
          }}
          placeholder="Select a stage"
          data-testid="select.signedQuoteStage"
          options={fieldsConfig.signedQuoteStage.options}
          label="Signed quote stage"
          description="Select the stage linked deals should move to in HubSpot when the related quote is signed."
          initialValue={fieldsConfig.signedQuoteStage.value}
          onChange={fieldsConfig.signedQuoteStage.onChange}
          validationErrors={fieldsConfig.signedQuoteStage.validationErrors}
          matchWidth
        />
      </SettingsSection>
    </Flex>
  )
}

type SettingsSectionProps = {
  title: string
  children: ReactNode
}

const SettingsSection: FC<SettingsSectionProps> = ({ title, children }) => (
  <Card width="100%">
    <Flex flexDirection="column" gap="16px">
      <Box {...Primary14SemiBold}>{title}</Box>
      <Flex
        backgroundColor={GreyGrey10}
        borderRadius="6px"
        padding="16px"
        gap="16px"
        flexDirection="column"
      >
        {children}
      </Flex>
    </Flex>
  </Card>
)

type SettingSwitchRowProps = ComponentProps<typeof SwitchLineInput> & {
  description?: string
}

const SettingSwitchRow: FC<SettingSwitchRowProps> = ({
  description,
  ...switchProps
}) => (
  <Box width={SETTINGS_FIELD_WIDTH}>
    <SwitchLineInput
      switchSide="left"
      styles={{ label: { ...Primary13SemiBold, color: GreyGrey70 } }}
      {...switchProps}
    />
    {description && (
      <Box {...Primary12Regular} marginTop="8px">
        {description}
      </Box>
    )}
  </Box>
)
